import clsx, { ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

const PREFIX = 'tw-';

/**
 * Every utility in this project carries the classic `tw-` prefix
 * (tailwind.config.js), which tailwind-merge v3 no longer understands — it
 * only knows Tailwind v4's `tw:` syntax. Left unconfigured, conflicting
 * classes were both emitted and stylesheet order silently decided which one
 * won, so a component override like `cn(base, 'tw-p-4')` worked or failed
 * depending on where the two rules happened to land in the compiled CSS.
 *
 * The parser hook strips the prefix (after variants, `!` and the negative
 * sign) before tailwind-merge reasons about conflicts; the emitted class
 * strings stay untouched. Anything without the prefix is marked external so a
 * custom class can never be swallowed as if it were a utility.
 */
const twMerge = extendTailwindMerge({
    experimentalParseClassName({ className, parseClassName }) {
        const lastColon = className.lastIndexOf(':');
        const variants = lastColon === -1 ? '' : className.slice(0, lastColon + 1);
        let base = lastColon === -1 ? className : className.slice(lastColon + 1);

        let important = '';
        if (base.startsWith('!')) {
            important = '!';
            base = base.slice(1);
        }

        let negative = '';
        if (base.startsWith('-')) {
            negative = '-';
            base = base.slice(1);
        }

        if (!base.startsWith(PREFIX)) {
            return { ...parseClassName(className), isExternal: true };
        }

        return parseClassName(variants + important + negative + base.slice(PREFIX.length));
    }
});

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}
